import React from 'react';
import { Home, Landmark, TrendingUp, PiggyBank, CreditCard, Gift, Briefcase, Sprout } from 'lucide-react';
import { EstateAssets } from '../../../types';
import { Fieldset, RadioGroup } from '../../ui';

interface Props {
    selectedCategories: string[];
    onChange: (categories: string[]) => void;
    assets: EstateAssets;
    onAssetsChange: (assets: EstateAssets) => void;
}

export const AssetDiscoveryStep: React.FC<Props> = ({
    selectedCategories,
    onChange,
    assets,
    onAssetsChange,
}) => {
    const toggle = (id: string) => {
        if (selectedCategories.includes(id)) {
            onChange(selectedCategories.filter(c => c !== id));
        } else {
            onChange([...selectedCategories, id]);
        }
    };

    const OPTIONS = [
        { id: 'property', label: "A home or other property", icon: Home, desc: "Houses, flats, land, foreign property" },
        { id: 'banks', label: "Bank or savings accounts", icon: Landmark, desc: "Current accounts, ISAs, fixed term bonds" },
        { id: 'investments', label: "Investments", icon: TrendingUp, desc: "Shares, stocks, unit trusts, crypto" },
        { id: 'pensions', label: "Pensions", icon: PiggyBank, desc: "Private pensions, workplace schemes" },
        { id: 'gifts', label: "Gifts (Last 7 Years)", icon: Gift, desc: "Cash or assets given away before death" },
        { id: 'debts', label: "Debts or Liabilities", icon: CreditCard, desc: "Mortgages, loans, credit cards, funeral costs" },
        { id: 'bpr', label: "Business Assets (BPR)", icon: Briefcase, desc: "Business interests, unquoted shares, partnership stakes — may qualify for 50–100% tax relief" },
        { id: 'apr', label: "Agricultural Land or Property (APR)", icon: Sprout, desc: "Farmland, farm buildings, farmhouses — may qualify for 50–100% Agricultural Property Relief" },
    ];

    // Phase 1 §2 — 3-state Trusts routing flag.
    // Maps to EstateAssets.trusts_or_life_interest:
    //   'yes'    -> { exists: true,  unsure: false }
    //   'no'     -> { exists: false, unsure: false }
    //   'unsure' -> { exists: null,  unsure: true  }
    const trustState = assets.trusts_or_life_interest;
    const trustsValue: string =
        trustState?.unsure === true ? 'unsure' :
            trustState?.exists === true ? 'yes' :
                trustState?.exists === false ? 'no' : '';

    const setTrusts = (v: string) => {
        const next = { ...(assets.trusts_or_life_interest ?? { exists: null }) };
        if (v === 'yes') {
            next.exists = true;
            next.unsure = false;
        } else if (v === 'no') {
            next.exists = false;
            next.unsure = false;
        } else if (v === 'unsure') {
            next.exists = null;
            next.unsure = true;
        }
        onAssetsChange({ ...assets, trusts_or_life_interest: next });
    };

    return (
        <div className="max-w-2xl mx-auto space-y-8">
            <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-ink">What did they own?</h2>
                <p className="text-ink3 mt-2">
                    Select everything that applies. We'll ask for details in the next steps.
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {OPTIONS.map(opt => {
                    const isSelected = selectedCategories.includes(opt.id);
                    return (
                        <div
                            key={opt.id}
                            onClick={() => toggle(opt.id)}
                            className={`
                                cursor-pointer p-4 rounded-xl border-2 transition-all flex items-start gap-4 select-none
                                ${isSelected
                                    ? 'border-accent bg-accent-soft/50 ring-2 ring-accent'
                                    : 'border-rule-soft bg-white hover:border-accent hover:bg-paper'}
                            `}
                        >
                            <div className={`p-2 rounded-lg ${isSelected ? 'bg-accent-soft0 text-white' : 'bg-paper text-ink3'}`}>
                                <opt.icon className="w-5 h-5" />
                            </div>
                            <div>
                                <h3 className={`font-bold text-sm ${isSelected ? 'text-ink' : 'text-ink2'}`}>
                                    {opt.label}
                                </h3>
                                <p className="text-xs text-ink3 mt-1 leading-snug">
                                    {opt.desc}
                                </p>
                            </div>
                            <div className="ml-auto mt-1">
                                <div className={`w-5 h-5 rounded-full border flex items-center justify-center
                                    ${isSelected ? 'bg-accent-soft0 border-accent' : 'border-rule bg-white'}
                                `}>
                                    {isSelected && <div className="w-2 h-2 rounded-full bg-white" />}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* §2 — Trusts (3-state routing flag, not a category list) */}
            <div className="border-t border-rule pt-8">
                <Fieldset
                    label="Trusts"
                    help="Assets held in trust, or trusts the deceased created. We'll ask for detail next if you say Yes or Not sure."
                >
                    <RadioGroup
                        name="trusts_existence"
                        value={trustsValue}
                        onChange={setTrusts}
                        inline
                        options={[
                            { value: 'yes', label: 'Yes' },
                            { value: 'no', label: 'No' },
                            { value: 'unsure', label: 'Not sure' },
                        ]}
                    />
                </Fieldset>
            </div>
        </div>
    );
};
